import { useQuery } from "@tanstack/react-query";
import {
  assertNever,
  BehavesAsControlCodeMappings,
  ControlCategories,
  ControlCodes,
  Group,
  getCategoryControls,
  getControlCategory,
  getControlCategoryName,
  getControlName,
  getEndpointCategoryPartnerEventCodes,
  getEndpointPartnerEventCodes,
  getPartnerEventName,
  getSCMControlCategories,
  getSCMControls,
  getValidControlCategory,
  identityProviderPartnerEventCodes,
  isDefensiveControl,
  isDiscoveredDevicesControl,
  isEndpointControl,
  isEndpointManagementControl,
  isHostFirewallControl,
  isIdentityProviderControl,
  isSCMControl,
  isVulnerabilityManagementControl,
  PartnerEvents,
  queries,
  SCMCategories,
  secondsInDay,
  secondsInHour,
  secondsInMinute,
  titleCase,
  useAuthConfig,
  useValidAccountControls,
  ValidControl,
  ValidControlCode,
  ValidControlCodes,
  ValidSCMCategory,
} from "@theprelude/core";
import { useMemo } from "react";
import { intersection, unique, uniqueBy } from "remeda";
import {
  Filter,
  getOdataId,
  OdataDefinition,
  OdataObjectValue,
  OdataOperator,
  OdataValue,
} from "@/lib/filter";
import { FormFilter, getDefaultCast } from "./filter-form";

const behavesAsControlCodes = new Set([
  ...Object.keys(BehavesAsControlCodeMappings).map(Number),
  ...Object.values(BehavesAsControlCodeMappings),
]);

const formatOperationalStateValue = (value: OdataValue) =>
  value === "na" ? "N/A" : titleCase(String(value).replaceAll(/[ _]+/g, " "));

export function getNamedControl(control: ValidControlCode) {
  const controlName = control && getControlName(control);
  const categoryName =
    control &&
    behavesAsControlCodes.has(control) &&
    getControlCategoryName(getControlCategory(control));

  return [controlName, categoryName].filter(Boolean).join(" - ");
}

export function getNamedInstance(control?: ValidControl) {
  const controlName = control && getControlName(control.id);
  const categoryName =
    control &&
    behavesAsControlCodes.has(control.id) &&
    getControlCategoryName(getControlCategory(control.id));
  const userControlName = control?.name?.trim();

  return [controlName, userControlName, categoryName]
    .filter(Boolean)
    .join(" - ");
}

export function prefixControlColumnLabel(
  label: string,
  control?: ValidControl,
) {
  return [getNamedInstance(control), label].filter(Boolean).join(" ");
}

export type FilterOption = {
  label: string;
  value: string | number | boolean | null;
};

export type AvailableFilter = {
  id: string;
  type: "text" | "number" | "duration" | "date" | "boolean";
  odata: OdataDefinition;
  label: string;
  group?: string;
  isArray?: boolean;
  operators: OdataOperator[];
  controls: ValidControlCode[] | "all";
  options?: FilterOption[];
  searchableOptions?: boolean;
  cast?: (filter: FormFilter) => Filter;
  parse?: (filter: Filter) => FormFilter;
};

export const exceptedOdata: OdataDefinition = {
  id: "excepted",
  scope: "excepted",
};
const exceptedFilter: AvailableFilter = {
  id: getOdataId(exceptedOdata),
  operators: ["is"],
  controls: "all",
  type: "boolean",
  label: "Excepted",
  odata: exceptedOdata,
  cast: (filter) => ({
    ...getDefaultCast(exceptedFilter.type)(filter),
    value: filter.value === "true",
  }),
  parse: (filter) => {
    return {
      ...filter,
      value: filter.value === true ? "true" : "false",
      ors: filter.ors?.map((or) => ({
        operator: or.operator,
        value: or.value === true ? "true" : "false",
      })),
    };
  },
};

export const hasAgentOdata: OdataDefinition = {
  id: "has_agent",
  scope: "instances",
};
const hasAgentFilter: AvailableFilter = {
  id: getOdataId(hasAgentOdata),
  operators: ["is"],
  controls: getCategoryControls(ControlCategories.VULN_MANAGER),
  type: "boolean",
  label: "Has Agent",
  odata: hasAgentOdata,
  cast: (filter) => ({
    ...getDefaultCast(hasAgentFilter.type)(filter),
    value:
      filter.value === "true" ? true : filter.value === null ? null : false,
  }),
  parse: (filter) => {
    return {
      ...filter,
      value:
        filter.value === null ? null : filter.value === true ? "true" : "false",
      ors: filter.ors?.map((or) => ({
        operator: or.operator,
        value: or.value === null ? null : or.value === true ? "true" : "false",
      })),
    };
  },
};

export const lastSeenOdata: OdataDefinition = {
  id: "last_seen",
  scope: "instances",
};
const lastSeenFilter: AvailableFilter = {
  id: getOdataId(lastSeenOdata),
  operators: ["less than", "greater than", "is", "is not"],
  controls: "all",
  type: "date",
  label: "Last Seen",
  odata: lastSeenOdata,
};

export const crossControlsLastSeenOdata: OdataDefinition = {
  id: "last_seen",
};

const crossControlsLastSeenFilter: AvailableFilter = {
  id: getOdataId(crossControlsLastSeenOdata),
  operators: ["less than", "greater than", "is", "is not"],
  controls: [
    ...getSCMControls(SCMCategories.ENDPOINT),
    ...getSCMControls(SCMCategories.USER),
  ],
  label: "Last Seen - Cross Controls",
  type: "date",
  odata: crossControlsLastSeenOdata,
};

const crossControlsDaysSinceLastSeenFilter = convertDurationAvailableFilter(
  {
    ...crossControlsLastSeenFilter,
    label: `Days since Last Seen - Cross Controls`,
  },
  crossControlsLastSeenOdata,
);

export const lastScanOdata: OdataDefinition = {
  id: "last_scan",
  scope: "instances",
};
const lastScanFilter: AvailableFilter = {
  id: getOdataId(lastScanOdata),
  operators: ["less than", "greater than", "is", "is not"],
  controls: getCategoryControls(ControlCategories.VULN_MANAGER),
  type: "date",
  label: "Last Scanned",
  odata: lastScanOdata,
};

export const ownershipOdata: OdataDefinition = {
  id: "device_ownership",
  scope: "instances",
};
const ownershipFilter: AvailableFilter = {
  id: getOdataId(ownershipOdata),
  operators: [
    "is",
    "is not",
    "contains",
    "does not contain",
    "matches",
    "does not match",
  ],
  controls: getSCMControls(SCMCategories.ENDPOINT),
  type: "text",
  label: "Ownership",
  odata: ownershipOdata,
};

export const platformsOdata: OdataDefinition = {
  id: "platforms",
};
const platformsFilter: AvailableFilter = {
  id: getOdataId(platformsOdata),
  operators: [
    "is",
    "is not",
    "contains",
    "does not contain",
    "matches",
    "does not match",
  ],
  controls: getSCMControls(SCMCategories.ENDPOINT),
  type: "text",
  label: "Platforms",
  odata: platformsOdata,
  isArray: true,
};

export const versionOdata: OdataDefinition = {
  id: "version",
  scope: "instances",
};
const versionFilter: AvailableFilter = {
  id: getOdataId(versionOdata),
  operators: [
    "is",
    "is not",
    "contains",
    "does not contain",
    "matches",
    "does not match",
  ],
  controls: getSCMControls(SCMCategories.ENDPOINT),
  type: "text",
  label: "Version",
  odata: versionOdata,
};

export const deviceTypeOdata: OdataDefinition = {
  id: "device_types",
};
const deviceTypeFilter: AvailableFilter = {
  id: getOdataId(deviceTypeOdata),
  operators: [
    "is",
    "is not",
    "contains",
    "does not contain",
    "matches",
    "does not match",
  ],
  controls: getSCMControls(SCMCategories.ENDPOINT),
  type: "text",
  label: "Device Types",
  odata: deviceTypeOdata,
  isArray: true,
};

export const userTypesOdata: OdataDefinition = {
  id: "user_types",
};
const userTypesFilter: AvailableFilter = {
  id: getOdataId(userTypesOdata),
  operators: [
    "is",
    "is not",
    "contains",
    "does not contain",
    "matches",
    "does not match",
  ],
  controls: getCategoryControls(ControlCategories.IDENTITY),
  type: "text",
  label: "User Types",
  odata: userTypesOdata,
  isArray: true,
};

export const inboxTypesOdata: OdataDefinition = {
  id: "inbox_types",
};
const inboxTypesFilter: AvailableFilter = {
  id: getOdataId(inboxTypesOdata),
  operators: [
    "is",
    "is not",
    "contains",
    "does not contain",
    "matches",
    "does not match",
  ],
  controls: getCategoryControls(ControlCategories.EMAIL),
  type: "text",
  label: "Inbox Types",
  odata: inboxTypesOdata,
  isArray: true,
};

export const domainOdata: OdataDefinition = {
  id: "domain",
};
const domainFilter: AvailableFilter = {
  id: getOdataId(domainOdata),
  operators: [
    "is",
    "is not",
    "contains",
    "does not contain",
    "matches",
    "does not match",
  ],
  controls: getCategoryControls(ControlCategories.EMAIL),
  type: "text",
  label: "Domain",
  odata: domainOdata,
};

export const principalNamesOdata: OdataDefinition = {
  id: "principal_names",
};
const principalNamesFilter: AvailableFilter = {
  id: getOdataId(principalNamesOdata),
  operators: [
    "is",
    "is not",
    "contains",
    "does not contain",
    "matches",
    "does not match",
  ],
  controls: getCategoryControls(ControlCategories.EMAIL),
  type: "text",
  label: "Principal Names",
  odata: principalNamesOdata,
  isArray: true,
};

export const statusesOdata: OdataDefinition = {
  id: "statuses",
};
const statusesFilter: AvailableFilter = {
  id: getOdataId(statusesOdata),
  operators: [
    "is",
    "is not",
    "contains",
    "does not contain",
    "matches",
    "does not match",
  ],
  controls: [
    ...getCategoryControls(ControlCategories.EMAIL),
    ...getCategoryControls(ControlCategories.IDENTITY),
  ],
  type: "text",
  label: "Statuses",
  odata: statusesOdata,
  isArray: true,
};

export const risksOdata: OdataDefinition = {
  id: "risks",
};
const risksFilter: AvailableFilter = {
  id: getOdataId(risksOdata),
  operators: [
    "is",
    "is not",
    "contains",
    "does not contain",
    "matches",
    "does not match",
  ],
  controls: [
    ...getCategoryControls(ControlCategories.EMAIL),
    ...getCategoryControls(ControlCategories.IDENTITY),
  ],
  type: "text",
  label: "Risks",
  odata: risksOdata,
  isArray: true,
};

export const emailOdata: OdataDefinition = {
  id: "email",
};
const emailFilter: AvailableFilter = {
  id: getOdataId(emailOdata),
  operators: [
    "is",
    "is not",
    "contains",
    "does not contain",
    "matches",
    "does not match",
  ],
  controls: [
    ...getCategoryControls(ControlCategories.EMAIL),
    ...getCategoryControls(ControlCategories.IDENTITY),
  ],
  type: "text",
  label: "Email",
  odata: emailOdata,
};

export const hostNameOdata: OdataDefinition = {
  id: "hostname",
};
const hostNameFilter: AvailableFilter = {
  id: getOdataId(hostNameOdata),
  operators: [
    "is",
    "is not",
    "contains",
    "does not contain",
    "matches",
    "does not match",
  ],
  controls: getSCMControls(SCMCategories.ENDPOINT),
  type: "text",
  label: "Hostname",
  odata: hostNameOdata,
};

export const serialNumberOdata: OdataDefinition = {
  id: "serial_number",
};
const serialNumberFilter: AvailableFilter = {
  id: getOdataId(serialNumberOdata),
  operators: [
    "is",
    "is not",
    "contains",
    "does not contain",
    "matches",
    "does not match",
  ],
  controls: getSCMControls(SCMCategories.ENDPOINT),
  type: "text",
  label: "Serial Number",
  odata: serialNumberOdata,
};

export const cloudAccountIdOdata: OdataDefinition = {
  id: "cloud_account_id",
};
const cloudAccountIdFilter: AvailableFilter = {
  id: getOdataId(cloudAccountIdOdata),
  operators: [
    "is",
    "is not",
    "contains",
    "does not contain",
    "matches",
    "does not match",
  ],
  controls: getSCMControls(SCMCategories.ENDPOINT),
  type: "text",
  label: "Cloud Account ID",
  odata: cloudAccountIdOdata,
};

export const cloudInstanceIdOdata: OdataDefinition = {
  id: "cloud_instance_id",
};
const cloudInstanceIdFilter: AvailableFilter = {
  id: getOdataId(cloudInstanceIdOdata),
  operators: [
    "is",
    "is not",
    "contains",
    "does not contain",
    "matches",
    "does not match",
  ],
  controls: getSCMControls(SCMCategories.ENDPOINT),
  type: "text",
  label: "Cloud Instance ID",
  odata: cloudInstanceIdOdata,
};

export const macAddressesOdata: OdataDefinition = {
  id: "mac_addresses",
};
const macAddressesFilter: AvailableFilter = {
  id: getOdataId(macAddressesOdata),
  operators: [
    "is",
    "is not",
    "contains",
    "does not contain",
    "matches",
    "does not match",
  ],
  controls: getSCMControls(SCMCategories.ENDPOINT),
  type: "text",
  label: "MAC Addresses",
  odata: macAddressesOdata,
  isArray: true,
};

export const cloudRegionOdata: OdataDefinition = {
  id: "cloud_region",
};

const cloudRegionFilter: AvailableFilter = {
  id: getOdataId(cloudRegionOdata),
  operators: [
    "is",
    "is not",
    "contains",
    "does not contain",
    "matches",
    "does not match",
  ],
  controls: getSCMControls(SCMCategories.ENDPOINT),
  type: "text",
  label: "Cloud Region",
  odata: cloudRegionOdata,
};

export const primaryUserEmailsOdata: OdataDefinition = {
  id: "primary_user_emails",
};
const primaryUserEmailsFilter: AvailableFilter = {
  id: getOdataId(primaryUserEmailsOdata),
  operators: [
    "is",
    "is not",
    "contains",
    "does not contain",
    "matches",
    "does not match",
  ],
  controls: getSCMControls(SCMCategories.ENDPOINT),
  type: "text",
  label: "Primary Users",
  odata: primaryUserEmailsOdata,
  isArray: true,
};

export const userDevicesOdata: OdataDefinition = {
  id: "devices",
  sort: ["length(devices)", "email"],
};
export const userDevicesFilter: AvailableFilter = {
  id: getOdataId(userDevicesOdata),
  operators: ["is", "is not", "less than", "greater than"],
  controls: getCategoryControls(ControlCategories.IDENTITY),
  type: "number",
  label: "Number of Devices",
  odata: userDevicesOdata,
  cast: (filter) => ({
    ...getDefaultCast(userDevicesFilter.type)(filter),
    fn: "length",
  }),
};

export const rfmOData: OdataDefinition = {
  id: "reduced_functionality_mode",
  scope: "instances",
};

const crowdstrikeRFMOdata: OdataDefinition = {
  ...rfmOData,
  with: { control: ControlCodes.CROWDSTRIKE },
};
const crowdstrikeRFMFilter: AvailableFilter = {
  id: getOdataId(crowdstrikeRFMOdata),
  operators: ["is"],
  controls: [ControlCodes.CROWDSTRIKE],
  type: "boolean",
  label: "Reduced Functionality Mode",
  odata: crowdstrikeRFMOdata,
  group: "Control Failures",
};

const defenderPassiveModeOdata: OdataDefinition = {
  ...rfmOData,
  with: { control: ControlCodes.DEFENDER },
};
const defenderPassiveModeFilter: AvailableFilter = {
  id: getOdataId(defenderPassiveModeOdata),
  operators: ["is"],
  controls: [ControlCodes.DEFENDER],
  type: "boolean",
  label: "Passive Mode",
  odata: defenderPassiveModeOdata,
  group: "Control Failures",
};

export const operationalStateOdata: OdataDefinition = {
  id: "operational_state",
  scope: "instances",
};
const operationalStateFilter: AvailableFilter = {
  id: getOdataId(operationalStateOdata),
  type: "text",
  label: "Operational State",
  operators: ["is", "is not"],
  controls: [ControlCodes.SENTINELONE],
  odata: operationalStateOdata,
  cast: ({ operator, ...filter }) => {
    if (operator === "") {
      throw new Error('Invalid operator: ""');
    }
    const value =
      filter.value === null
        ? null
        : filter.value
            .trim()
            .toLowerCase()
            .replaceAll(/[ _]+/g, "_")
            .replaceAll(/\W*/g, "");
    return { ...filter, operator, value };
  },
  parse: (filter) => ({
    ...filter,
    value: formatOperationalStateValue(filter.value),
    ors: filter.ors?.map((or) => ({
      operator: or.operator,
      value: formatOperationalStateValue(or.value),
    })),
  }),
};

export const noEDROData: OdataDefinition = {
  id: "missing_edr",
};
const noEDRFilter: AvailableFilter = {
  id: getOdataId(noEDROData),
  operators: ["is"],
  controls: getSCMControls(SCMCategories.ENDPOINT),
  type: "boolean",
  label: "No EDR",
  odata: noEDROData,
  group: "Control Failures",
};

export const missingHostFirewallPolicyOData: OdataDefinition = {
  id: "no_host_firewall_policy",
  scope: "instances",
};

export const noHostFirewallData: OdataDefinition = {
  id: "missing_host_firewall",
};

export const noEndpointManagementOData: OdataDefinition = {
  id: "missing_asset_manager",
};
const noEndpointManagementFilter: AvailableFilter = {
  id: getOdataId(noEndpointManagementOData),
  operators: ["is"],
  controls: getSCMControls(SCMCategories.ENDPOINT),
  type: "boolean",
  label: "No Endpoint Management",
  odata: noEndpointManagementOData,
  group: "Control Failures",
};

export const noServerManagementOData: OdataDefinition = {
  id: "missing_server_manager",
};
const noServerManagementFilter: AvailableFilter = {
  id: getOdataId(noServerManagementOData),
  operators: ["is"],
  controls: getSCMControls(SCMCategories.ENDPOINT),
  type: "boolean",
  label: "No Server Management",
  odata: noServerManagementOData,
  group: "Control Failures",
};

export const noVulnerabilityManagementOData: OdataDefinition = {
  id: "missing_vuln_manager",
};
const noVulnerabilityManagementFilter: AvailableFilter = {
  id: getOdataId(noVulnerabilityManagementOData),
  operators: ["is"],
  controls: getSCMControls(SCMCategories.ENDPOINT),
  type: "boolean",
  label: "No Vulnerability Management",
  odata: noVulnerabilityManagementOData,
  group: "Control Failures",
};

export const missingScanOData: OdataDefinition = {
  id: "missing_scan",
  scope: "instances",
};
const missingScanFilter: AvailableFilter = {
  id: getOdataId(missingScanOData),
  operators: ["is"],
  controls: getCategoryControls(ControlCategories.VULN_MANAGER),
  type: "boolean",
  label: "Missing Vulnerability Scan",
  odata: missingScanOData,
  group: "Control Failures",
};

export const outOfDateScanOData: OdataDefinition = {
  id: "out_of_date_scan",
  scope: "instances",
};
const outOfDateScanFilter: AvailableFilter = {
  id: getOdataId(outOfDateScanOData),
  operators: ["is"],
  controls: getCategoryControls(ControlCategories.VULN_MANAGER),
  type: "boolean",
  label: "Out-of-Date Vulnerability Scan",
  odata: outOfDateScanOData,
  group: "Control Failures",
};

export const missingMFAOData: OdataDefinition = {
  id: "missing_mfa",
  scope: "instances",
};
const missingMFAFilter: AvailableFilter = {
  id: getOdataId(missingMFAOData),
  operators: ["is"],
  controls: getCategoryControls(ControlCategories.IDENTITY),
  type: "boolean",
  label: "Missing MFA",
  odata: missingMFAOData,
  group: "Control Failures",
};

export const startTimeOdata: OdataDefinition = {
  id: "start_time",
  scope: "failures",
};
const startTimeFilter: AvailableFilter = {
  id: getOdataId(startTimeOdata),
  operators: ["less than", "greater than", "is", "is not"],
  controls: "all",
  type: "duration",
  label: "Days in State",
  odata: startTimeOdata,
};

export const rfmStartTimeOdata: OdataDefinition = {
  ...startTimeOdata,
  with: { event: PartnerEvents.REDUCED_FUNCTIONALITY_MODE },
};
export const mfaStartTimeOdata: OdataDefinition = {
  ...startTimeOdata,
  with: { event: PartnerEvents.MISSING_MFA },
};
export const noAMStartTimeOdata: OdataDefinition = {
  ...startTimeOdata,
  with: { event: PartnerEvents.NO_ASSET_MANAGER },
};
export const noEDRStartTimeOData: OdataDefinition = {
  ...startTimeOdata,
  with: { event: PartnerEvents.NO_EDR },
};
export const noVMStartTimeOdata: OdataDefinition = {
  ...startTimeOdata,
  with: { event: PartnerEvents.NO_VULN_MANAGER },
};

export function getOdataDurationString(value: OdataValue) {
  if (typeof value === "boolean" || typeof value === "object") {
    throw new TypeError(
      "Expected filter value to be a string or number when casting",
    );
  }
  let totalSeconds =
    typeof value === "string" ? Number.parseInt(value, 10) : value;
  const days = Math.floor(totalSeconds / secondsInDay);
  totalSeconds -= days * secondsInDay;
  const hours = Math.floor(totalSeconds / secondsInHour);
  totalSeconds -= hours * secondsInHour;
  const minutes = Math.floor(totalSeconds / secondsInMinute);
  totalSeconds -= minutes * secondsInMinute;
  const seconds = totalSeconds;
  return `P${days}DT${hours}H${minutes}M${seconds}S`;
}

export function getDurationLastSeenSeconds(value: OdataValue) {
  if (typeof value !== "string") {
    throw new TypeError("Expected filter value to be a string");
  }
  const regex = /duration'P(\d+D)?T?(\d+H)?(\d+M)?(\d+S)?'/;
  const matches = value.match(regex);
  if (!matches || !matches[1]) {
    throw new Error("Unable to parse filter string");
  }

  const days = Number.parseInt(matches[1] ?? "0", 10);
  const hours = Number.parseInt(matches[2] ?? "0", 10);
  const minutes = Number.parseInt(matches[3] ?? "0", 10);
  const seconds = Number.parseInt(matches[4] ?? "0", 10);

  const totalSeconds = Math.floor(
    days * secondsInDay +
      hours * secondsInHour +
      minutes * secondsInMinute +
      seconds,
  );

  return totalSeconds;
}

export function flipOperator(operator: OdataOperator) {
  switch (operator) {
    case "greater than": {
      return "less than";
    }
    case "less than": {
      return "greater than";
    }
    case "is": {
      return "is not";
    }
    case "is not": {
      return "is";
    }
    case "contains": {
      return "does not contain";
    }
    case "does not contain": {
      return "contains";
    }
    case "matches": {
      return "does not match";
    }
    case "does not match": {
      return "matches";
    }
    default: {
      return assertNever(operator);
    }
  }
}

export function prefixDurationId(id: string) {
  return isDurationId(id) ? id : `duration_${id}`;
}

export function isDurationId(id: string) {
  return id.includes("duration_");
}

export function stripDurationId(id: string) {
  return id.replace("duration_", "");
}

export function convertDurationFilter(
  odata: OdataDefinition,
  operator: OdataOperator,
  value: number | null,
): Filter {
  // Handle null values for 'is' and 'is not' operators
  if (value === null && (operator === "is" || operator === "is not")) {
    return {
      ...odata,
      operator,
      value: null,
    };
  }

  return {
    ...odata,
    operator,
    value: {
      type: "raw",
      value: `now() sub duration'${getOdataDurationString(value)}'`,
    },
  };
}

function transformDurationFilter(
  operator: OdataOperator,
  value: string | number,
): {
  operator: OdataOperator;
  value: OdataObjectValue;
} {
  const newOperator =
    operator !== "is" && operator !== "is not"
      ? flipOperator(operator)
      : operator;
  return {
    operator: newOperator,
    value: {
      type: "raw",
      value: `now() sub duration'${getOdataDurationString(value)}'`,
    },
  };
}

function convertDurationAvailableFilter(
  filter: AvailableFilter,
  odata: OdataDefinition,
): AvailableFilter {
  return {
    ...filter,
    id: getOdataId({ ...odata, id: prefixDurationId(odata.id) }),
    type: "duration",
    cast: (filter) => {
      const baseResult = getDefaultCast(userDevicesFilter.type)(filter);

      if (filter.operator === "") {
        throw new Error('Invalid operator: ""');
      }

      // Handle null values
      if (filter.value === null) {
        return {
          ...baseResult,
          operator: filter.operator,
          value: null,
          ors: baseResult.ors?.map((or) => {
            if (or.value === null) {
              return {
                operator: or.operator,
                value: null,
              };
            }
            return transformDurationFilter(
              or.operator,
              or.value?.toString() ?? "",
            );
          }),
        };
      }

      // Handle non-null values - use transformDurationFilter for all operators
      return {
        ...baseResult,
        ...transformDurationFilter(
          filter.operator,
          filter.value?.toString() ?? "",
        ),
        ors: baseResult.ors?.map((or) => {
          if (or.value === null) {
            return {
              operator: or.operator,
              value: null,
            };
          }
          return transformDurationFilter(
            or.operator,
            or.value?.toString() ?? "",
          );
        }),
      };
    },
    parse: (filter) => {
      // Handle null values
      if (filter.value === null) {
        return {
          ...filter,
          operator: filter.operator,
          value: null,
          ors: filter.ors?.map((or) => {
            if (or.value === null) {
              return {
                operator: or.operator,
                value: null,
              };
            }
            if (typeof or.value !== "object" || or.value?.type !== "raw") {
              throw new TypeError(
                "Expected duration filter value to be a raw odata object",
              );
            }
            // For non-null values, flip back only non-"is"/"is not" operators
            const parsedOperator =
              or.operator === "is" || or.operator === "is not"
                ? or.operator
                : flipOperator(or.operator);
            return {
              ...or,
              operator: parsedOperator,
              value: getDurationLastSeenSeconds(or.value.value).toString(),
            };
          }),
        };
      }

      // Handle non-null values
      if (typeof filter.value !== "object" || filter.value?.type !== "raw") {
        throw new TypeError(
          "Expected duration filter value to be a raw odata object",
        );
      }

      // For non-null values, flip back only non-"is"/"is not" operators
      const parsedOperator =
        filter.operator === "is" || filter.operator === "is not"
          ? filter.operator
          : flipOperator(filter.operator);

      return {
        ...filter,
        operator: parsedOperator,
        value: getDurationLastSeenSeconds(filter.value.value).toString(),
        ors: filter.ors?.map((or) => {
          if (or.value === null) {
            return {
              operator: or.operator,
              value: null,
            };
          }
          if (typeof or.value !== "object" || or.value?.type !== "raw") {
            throw new TypeError(
              "Expected duration filter value to be a raw odata object",
            );
          }
          // For non-null OR values, flip back only non-"is"/"is not" operators
          const parsedOrOperator =
            or.operator === "is" || or.operator === "is not"
              ? or.operator
              : flipOperator(or.operator);
          return {
            ...or,
            operator: parsedOrOperator,
            value: getDurationLastSeenSeconds(or.value.value).toString(),
          };
        }),
      };
    },
  };
}

export const durationLastSeenFilter: AvailableFilter =
  convertDurationAvailableFilter(
    {
      id: getOdataId(lastSeenOdata),
      operators: ["less than", "greater than", "is", "is not"],
      controls: "all",
      type: "duration",
      label: "Days since Last Seen",
      group: "Control Attributes",
      odata: lastSeenOdata,
    },
    lastSeenOdata,
  );

export const controlsOdata: OdataDefinition = {
  id: "controls",
};
const controlsFilter: AvailableFilter = {
  id: getOdataId(controlsOdata),
  operators: ["is", "is not", "contains", "does not contain"],
  type: "number",
  isArray: true,
  options: Object.values(ValidControlCodes)
    .filter(isSCMControl)
    .map((control) => ({
      label: getNamedControl(control),
      value: control,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)),
  controls: "all",
  label: "Controls",
  odata: controlsOdata,
};

export const controlInstancesOdata: OdataDefinition = {
  id: "instance_ids",
};
const controlInstancesFilter = (controls: ValidControl[]): AvailableFilter => ({
  id: getOdataId(controlInstancesOdata),
  operators: ["is", "is not", "contains", "does not contain"],
  type: "text",
  isArray: true,
  options: uniqueBy(
    controls
      .map((control) => ({
        label: getNamedInstance(control),
        value: control.instance_id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    (control) => control.value,
  ),
  controls: "all",
  label: "Control Instances",
  odata: controlInstancesOdata,
});

export const groupNameOdata: OdataDefinition = {
  id: "name",
  scope: "groups",
};
const groupsFilter = (groups: Group[]): AvailableFilter => ({
  id: getOdataId(groupNameOdata),
  operators: [
    "is",
    "is not",
    "contains",
    "does not contain",
    "matches",
    "does not match",
  ],
  options: uniqueBy(
    groups
      .map((g) => ({
        label: g.name,
        value: g.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    (g) => g.value,
  ),
  searchableOptions: true,
  controls: [
    ...getSCMControls(SCMCategories.ENDPOINT),
    ...getSCMControls(SCMCategories.INBOX),
    ...getSCMControls(SCMCategories.USER),
  ],
  type: "text",
  label: "Groups",
  odata: groupNameOdata,
});

function endpointControlStartTimeFilters(
  controls: ValidControl[],
): AvailableFilter[] {
  return [
    ...controls.flatMap((control) => {
      const category = getValidControlCategory(control.id);
      const eventCodes = getEndpointCategoryPartnerEventCodes(category);
      return eventCodes.map((event) => {
        const odata: OdataDefinition = {
          ...startTimeOdata,
          with: {
            control: control.id,
            instance_id: control.instance_id,
            event,
          },
        };

        return convertDurationAvailableFilter(
          {
            ...startTimeFilter,
            id: getOdataId(odata),
            label: prefixControlColumnLabel(
              `Days in ${getPartnerEventName(event)}`,
              control,
            ),
            group: getNamedControl(control.id),
            odata,
            controls: getSCMControls(SCMCategories.ENDPOINT),
          },
          odata,
        );
      });
    }),
    ...getEndpointPartnerEventCodes(undefined).map((event) => {
      const odata: OdataDefinition = {
        ...startTimeOdata,
        with: { event },
      };
      return convertDurationAvailableFilter(
        {
          ...startTimeFilter,
          id: getOdataId(odata),
          label: `Days in ${getPartnerEventName(event)}`,
          odata,
          group: "Control Failures",
          controls: getSCMControls(SCMCategories.ENDPOINT),
        },
        odata,
      );
    }),
  ];
}

function identityControlStartTimeFilters(controls: ValidControl[]) {
  return controls.flatMap((control) => {
    return identityProviderPartnerEventCodes.map((event) => {
      const odata: OdataDefinition = {
        ...startTimeOdata,
        with: { control: control.id, instance_id: control.instance_id, event },
      };
      return convertDurationAvailableFilter(
        {
          ...startTimeFilter,
          id: getOdataId(odata),
          label: prefixControlColumnLabel(
            `Days in ${getPartnerEventName(event)}`,
            control,
          ),
          group: getNamedControl(control.id),
          odata,
          controls: getSCMControls(SCMCategories.USER),
        },
        odata,
      );
    });
  });
}

function createControlFilter<T extends AvailableFilter>(
  baseFilter: T,
  controls: ValidControl[],
  odataBase: OdataDefinition,
  isDuration = false,
): AvailableFilter[] {
  return controls.map((control) => {
    const odata: OdataDefinition = {
      ...odataBase,
      with: { control: control.id, instance_id: control.instance_id },
    };
    const filterConfig = {
      ...baseFilter,
      id: getOdataId(odata),
      label: isDuration
        ? prefixControlColumnLabel(`Days since ${baseFilter.label}`, control)
        : prefixControlColumnLabel(baseFilter.label, control),
      group: getNamedControl(control.id),
      odata,
    };

    return isDuration
      ? convertDurationAvailableFilter(filterConfig, odata)
      : filterConfig;
  });
}

const endpointControlFilters = (
  controls: ValidControl[],
): AvailableFilter[] => {
  const endpointControls = controls.filter((c) => isEndpointControl(c.id));
  const vulnerabilityControls = controls.filter((c) =>
    isVulnerabilityManagementControl(c.id),
  );
  const lastScanFilters = createControlFilter(
    lastScanFilter,
    vulnerabilityControls,
    lastScanOdata,
  );
  const hasAgentFilters = createControlFilter(
    hasAgentFilter,
    vulnerabilityControls,
    hasAgentOdata,
  );
  const versionFilters = createControlFilter(
    versionFilter,
    controls.filter(
      (c) =>
        isDefensiveControl(c.id) ||
        isDiscoveredDevicesControl(c.id) ||
        isEndpointManagementControl(c.id) ||
        isHostFirewallControl(c.id),
    ),
    versionOdata,
  );
  const ownershipFilters = createControlFilter(
    ownershipFilter,
    controls.filter(
      (c) =>
        isEndpointManagementControl(c.id) ||
        isDiscoveredDevicesControl(c.id) ||
        isHostFirewallControl(c.id),
    ),
    ownershipOdata,
  );

  return [
    ...endpointControlStartTimeFilters(endpointControls),
    ...lastScanFilters,
    ...versionFilters,
    ...ownershipFilters,
    ...hasAgentFilters,
  ];
};

const userControlFilters = (controls: ValidControl[]): AvailableFilter[] => {
  const identityControls = controls.filter((c) =>
    isIdentityProviderControl(c.id),
  );
  return [...identityControlStartTimeFilters(identityControls)];
};

function isLastSeenControl(control: ValidControl) {
  return (
    isEndpointControl(control.id) ||
    isIdentityProviderControl(control.id) ||
    isHostFirewallControl(control.id)
  );
}
function controlFilters(controls: ValidControl[]): AvailableFilter[] {
  const lastSeenControls = controls.filter(isLastSeenControl);
  const lastSeenFilters = createControlFilter(
    lastSeenFilter,
    lastSeenControls,
    lastSeenOdata,
  );
  const daysSincelastSeenFilters = createControlFilter(
    lastSeenFilter,
    lastSeenControls,
    lastSeenOdata,
    true,
  );
  return [
    ...lastSeenFilters,
    ...daysSincelastSeenFilters,
    ...endpointControlFilters(controls),
    ...userControlFilters(controls),
  ];
}

const getSCMFilters = (
  controls: ValidControl[],
  controlCodes: ValidControlCode[],
  groups: Group[],
): AvailableFilter[] => {
  const filters = [
    cloudAccountIdFilter,
    cloudInstanceIdFilter,
    cloudRegionFilter,
    controlsFilter,
    controlInstancesFilter(controls),
    crowdstrikeRFMFilter,
    defenderPassiveModeFilter,
    deviceTypeFilter,
    emailFilter,
    exceptedFilter,
    hostNameFilter,
    inboxTypesFilter,
    macAddressesFilter,
    missingMFAFilter,
    missingScanFilter,
    noEDRFilter,
    noEndpointManagementFilter,
    noServerManagementFilter,
    noVulnerabilityManagementFilter,
    operationalStateFilter,
    outOfDateScanFilter,
    platformsFilter,
    primaryUserEmailsFilter,
    serialNumberFilter,
    userTypesFilter,
    statusesFilter,
    risksFilter,
    userDevicesFilter,
    groupsFilter(groups),
    ...controlFilters(controls),
    principalNamesFilter,
    domainFilter,
    crossControlsLastSeenFilter,
    crossControlsDaysSinceLastSeenFilter,
  ];

  return filters
    .filter((filter) => {
      const filterControls = filter.controls;
      // include filter if it's explicitly included for all controls
      if (filterControls === "all") {
        return true;
      }
      // include filter if the user has all the controls the filter specifies
      const matches = intersection(
        controls.map((c) => c.id),
        filterControls,
      );
      if (matches.length > 0) {
        return true;
      }
      // include filter if it specifies all the controls for a set of control category
      return (
        controlCodes.length > 0 &&
        controlCodes.every((controlCode) =>
          filterControls.includes(controlCode),
        )
      );
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

const defaultControls: never[] = [];

export const useControlFilters = (
  controlCodes?: ValidControlCode[],
): AvailableFilter[] => {
  const authConfig = useAuthConfig();
  const { data: validControls = defaultControls } = useValidAccountControls();
  const { data: groups = [] } = useQuery(queries.scm.groups(authConfig));

  return useMemo(() => {
    const controls = controlCodes
      ? validControls.filter((c) => controlCodes.includes(c.id))
      : validControls;
    return getSCMFilters(controls, controlCodes ?? [], groups);
  }, [controlCodes, validControls, groups]);
};

const scmFilterControls = {
  endpoint: getSCMControls(SCMCategories.ENDPOINT),
  inbox: getSCMControls(SCMCategories.INBOX),
  user: getSCMControls(SCMCategories.USER),
};

export const useSCMFilters = () => {
  const endpointFilters = useControlFilters(scmFilterControls.endpoint);
  const inboxFilters = useControlFilters(scmFilterControls.inbox);
  const userFilters = useControlFilters(scmFilterControls.user);

  const filters = useMemo(
    () => ({
      endpoints: endpointFilters.filter((f) =>
        isInEveryCategory(f.controls, SCMCategories.ENDPOINT),
      ),
      inboxes: inboxFilters.filter((f) =>
        isInEveryCategory(f.controls, SCMCategories.INBOX),
      ),
      users: userFilters.filter((f) =>
        isInEveryCategory(f.controls, SCMCategories.USER),
      ),
    }),
    [endpointFilters, inboxFilters, userFilters],
  );

  return filters;
};

const isInEveryCategory = (
  filterControls: ValidControlCode[] | "all",
  scmCategory: ValidSCMCategory,
) => {
  if (filterControls === "all") {
    return true;
  }

  const controlCategories = getSCMControlCategories(scmCategory);
  const filterControlCategories = unique(
    filterControls.map(getControlCategory),
  );
  const intersectCategories = intersection(
    controlCategories,
    filterControlCategories,
  );

  return controlCategories.length === intersectCategories.length;
};
